import React, { useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import "./AssetGis.scss";
import { MapContainer, Marker, Popup, TileLayer, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { APP_BASE_URL, GET_ASSETS } from "../Common/EndPoints";
import axios from "axios";
import { useSelector } from "react-redux";
import Loader from "../Components/Loader/Loader";
import { useImmer } from "use-immer";
import { Alert, Autocomplete, Button, Chip, TextField } from "@mui/material";
import { ActiveAlarmsData } from "../Components/Alarms/ActiveAlarms";
import { useGetDeviceTypesQuery, useGetDevicesNameQuery } from "../features/SearchFilters/searchFiltersSlice";
import { getDeviceNameSearchParams } from "../features/SearchFilters/searchFiltersUtils";
import { getMarkerIcon } from "./Utils/Constants";
import GisPopup from "./Utils/GisPopup.js/GisPopup";
import {
  isFullscreenSupported,
  getFullscreenElement,
  requestFullscreen,
  exitFullscreen,
  addFullscreenListener,
  removeFullscreenListener,
} from "./../Utils/fullScreenUtils";
import toast from "react-hot-toast";

// ======================= KEY CHANGE 1: RESTORE THE CORRECT SELECTOR IMPORT =======================
import { selectedCustomersIdsSelector } from "../features/authentication/auth-slice";
// =================================================================================================

// --- Helper Components (No changes needed here) ---
const RenderAssetMarker = ({ deviceData, selectedAsset }) => {
  const { latitude, longitude, deviceType, deviceid } = deviceData;
  return (
    <Marker position={[latitude, longitude]} icon={getMarkerIcon(deviceType.type, selectedAsset, deviceid)}>
      <Popup>
        <GisPopup deviceData={deviceData} />
      </Popup>
    </Marker>
  );
};

const PanToCoordinate = ({ lat, lng, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (lat && lng) map?.flyTo?.([lat, lng], zoom);
  }, [lat, lng, zoom, map]);
  return null;
};

// --- Initial State Constants ---
const intialAssets = [];
const initCords = { center: [20.5937, 78.9629], zoom: 5 };
const iniFilters = { selectedCustomerIds: [], assetTypes: [] };

// --- Main Component ---
function AssetGis() {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const gisContainerRef = useRef(null);
  const isInitializedFromUrl = useRef(false); // Ref to prevent re-initialization

  const [assets, setAssets] = useState(intialAssets);
  const [loader, setLoader] = useState(false);
  const [selectedStationCords, setSelectedStationCords] = useState(initCords);
  const [currCords, setCurrCords] = useImmer(initCords);
  const [filters, setFilters] = useImmer(iniFilters);
  const [selectedAsset, setSelectedAsset] = useState(null);

  // ======================= KEY CHANGE 2: USE THE CORRECT SELECTOR =======================
  const customerIdList = useSelector(selectedCustomersIdsSelector);
  // ======================================================================================

  const { selectedCustomersIdName = [] } = useContext(ActiveAlarmsData);

  // --- RTK Query Hooks ---
  const { data: gearTypesList = [] } = useGetDeviceTypesQuery(null);
  const { data: deviceNamesList = [] } = useGetDevicesNameQuery(
    getDeviceNameSearchParams({
      customerIdList,
      filterState: {
        customer_ids: filters.selectedCustomerIds,
        device_type_ids: filters.assetTypes,
      },
    }),
  );

  // --- Memoized Station List ---
  const permittedCustomerIds = useMemo(() => {
    if (!selectedCustomersIdName) return [];
    return selectedCustomersIdName.map((c) => ({ id: c.customerId, value: c.customerName }));
  }, [selectedCustomersIdName]);


  // --- API Functions wrapped in useCallback for stability ---
  const getAssetsList = useCallback(
    (filtersParams) => {
      if (!filtersParams?.selectedCustomerIds?.length) {
        toast.error("Please select at least one station to filter.");
        return;
      }
      setLoader(true);
      const params = {
        customerIds: filtersParams.selectedCustomerIds.map((stn) => stn.id).toString(),
        type_id: (filtersParams.assetTypes || []).map((type) => type.id).toString(),
        is_active: true,
        with_type: true,
      };
      axios
        .get(`${APP_BASE_URL}${GET_ASSETS}`, { params })
        .then((res) => {
          const responseData = res.data.data;
          setAssets(responseData);
          const firstAsset = responseData?.[0];
          if (firstAsset?.latitude && firstAsset?.longitude) {
            const newCords = { center: [firstAsset.latitude, firstAsset.longitude], zoom: 14 };
            setCurrCords(newCords);
            setSelectedStationCords(newCords);
          }
        })
        .catch(() => toast.error("Failed to fetch assets."))
        .finally(() => setLoader(false));
    },
    [setCurrCords],
  );

  const findAsset = useCallback(
    (asset) => {
      if (!asset?.id) return;
      setLoader(true);
      const params = { deviceIds: asset.id, with_type: true, with_attachments: true };
      axios
        .get(`${APP_BASE_URL}${GET_ASSETS}`, { params })
        .then((res) => {
          const assetData = res.data.data?.[0];
          if (assetData?.latitude && assetData?.longitude) {
            setCurrCords((draft) => {
              draft.center = [assetData.latitude, assetData.longitude];
              draft.zoom = 20;
            });
          } else {
            toast.error("Asset location data not found.");
          }
        })
        .catch(() => toast.error("Failed to find the specified asset."))
        .finally(() => setLoader(false));
    },
    [setCurrCords],
  );


  // --- The Core Initialization Logic (Handles URL redirection) ---
  useEffect(() => {
    // Guard 1: Wait until the list of permitted stations has loaded.
    if (permittedCustomerIds.length === 0) return;

    // Guard 2: If we have already initialized from the URL, do nothing.
    if (isInitializedFromUrl.current) return;

    const params = new URLSearchParams(window.location.search);
    const stationIdsParam = params.get("station_ids");
    const assetIdParam = params.get("asset_id");
    const assetNameParam = params.get("asset_name");

    // Guard 3: If no relevant params exist, exit and let the component behave normally.
    if (!stationIdsParam && !assetIdParam) return;

    // Set the flag to true to prevent this from running again.
    isInitializedFromUrl.current = true;

    if (stationIdsParam) {
      const stationIdsToFind = stationIdsParam.split(',');
      const initialStations = permittedCustomerIds.filter(station => stationIdsToFind.includes(station.id));

      if (initialStations.length > 0) {
        const newFilters = { selectedCustomerIds: initialStations, assetTypes: [] };
        setFilters(newFilters);
        getAssetsList(newFilters);
      } else {
        toast.error("The station specified in the URL could not be found.");
      }
    }

    if (assetIdParam && assetNameParam) {
      const assetObject = { id: assetIdParam, value: assetNameParam };
      setSelectedAsset(assetObject);
      findAsset(assetObject);
    }
  }, [permittedCustomerIds, getAssetsList, findAsset, setFilters]);


  // --- Fullscreen Logic ---
  const fullscreenEnabled = useMemo(() => isFullscreenSupported(), []);
  const toggleFullscreen = () => {
    if (!getFullscreenElement()) requestFullscreen(gisContainerRef.current);
    else exitFullscreen();
  };
  useEffect(() => {
    const onFullscreenChange = () => setIsFullscreen(!!getFullscreenElement());
    addFullscreenListener(onFullscreenChange);
    return () => removeFullscreenListener(onFullscreenChange);
  }, []);


  // --- Render Logic ---
  return (
    <div className="gis-conatiner" ref={gisContainerRef} style={{ backgroundColor: isFullscreen ? 'white' : 'transparent' }}>
      <Alert size="small" severity="error" variant="outlined">
        <strong>Please choose a station to view its assets. You can select a maximum of 5 stations at a time.</strong>
      </Alert>
      <div className="gis-filters">
        <div className="filter-inputs">
          <Autocomplete
            size="small"
            multiple
            options={permittedCustomerIds}
            value={filters.selectedCustomerIds}
            onChange={(_, newValue) => {
              if (newValue.length > 5) toast.error("You can only select 5 stations");
              else setFilters((state) => { state.selectedCustomerIds = newValue; });
            }}
            disableCloseOnSelect
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={(option) => option.value || ""}
            renderInput={(params) => <TextField {...params} label="Stations" />}
            renderTags={(value, getTagProps) => {
              if (!value || value.length === 0) return null;
              return (<>
                  <Chip size="small" variant="outlined" label={value[0].value} {...getTagProps({ index: 0 })} />
                  {value.length > 1 && <Chip size="small" label={`+${value.length - 1}`} />}
              </>);
            }}
            className="filter-input auto-complete"
          />
          <Autocomplete
            size="small"
            multiple
            options={gearTypesList}
            value={filters.assetTypes}
            onChange={(_, newValue) => setFilters((state) => { state.assetTypes = newValue; })}
            disableCloseOnSelect
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={(option) => option.value || ""}
            renderInput={(params) => <TextField {...params} label="Asset types" />}
            renderTags={(value, getTagProps) => {
              if (!value || value.length === 0) return null;
              return (<>
                  <Chip size="small" variant="outlined" label={value[0].value} {...getTagProps({ index: 0 })} />
                  {value.length > 1 && <Chip size="small" label={`+${value.length - 1}`} />}
              </>);
            }}
            className="filter-input auto-complete assetType"
          />
          <Button onClick={() => getAssetsList(filters)} variant="contained">Filter</Button>
          <Button
            onClick={() => {
              setFilters(iniFilters);
              setAssets(intialAssets);
              setCurrCords(initCords);
              setSelectedAsset(null);
            }}
            variant="outlined"
          >
            Clear
          </Button>
        </div>
        <div className="find-asset-inputs">
          <Autocomplete
            size="small"
            options={deviceNamesList}
            value={selectedAsset}
            onChange={(_, newValue) => setSelectedAsset(newValue)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={(option) => option.value || ""}
            renderInput={(params) => <TextField {...params} label="Assets" />}
            className="filter-input auto-complete assetType"
          />
          <Button onClick={() => findAsset(selectedAsset)} variant="contained">Find</Button>
        </div>
        {fullscreenEnabled && (
          <Button variant="outlined" onClick={toggleFullscreen}>
            {isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
          </Button>
        )}
      </div>
      <div className="map-container">
        {loader && <Loader />}
        <MapContainer center={currCords.center} zoom={currCords.zoom} scrollWheelZoom={true} style={{ height: "100%", width: "100%" }}>
          <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" minZoom={5} maxZoom={20} />
          <PanToCoordinate lat={currCords.center[0]} lng={currCords.center[1]} zoom={currCords.zoom} />
          {assets
            .filter((asset) => (selectedAsset ? selectedAsset.id === asset.deviceid : true))
            .map((asset) => (<RenderAssetMarker key={asset.deviceid} deviceData={asset} selectedAsset={selectedAsset} />))
          }
        </MapContainer>
      </div>
    </div>
  );
}

export default AssetGis;